import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { useIsMobile } from '@/components/ui/use-mobile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DollarSign, Loader2, Globe } from 'lucide-react';
import { TeamMember } from '@/types/team';
import { LeadSource, DEAL_TYPES, DealType, PRIORITY_TIERS } from '@/types/crm';
import { projectAPI } from '@/utils/supabase/client';

interface ClientFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

// 44px touch targets on phones, standard 36px from sm up
const inputSize = 'h-11 sm:h-9';
const selectSize = 'data-[size=default]:h-11 sm:data-[size=default]:h-9';

export function ClientForm({ open, onOpenChange, onSuccess }: ClientFormProps) {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [owners, setOwners] = useState<TeamMember[]>([]);
  const [leadSources, setLeadSources] = useState<LeadSource[]>([]);
  const [formData, setFormData] = useState({
    company_name: '',
    primary_contact_name: '',
    primary_contact_email: '',
    primary_contact_phone: '',
    website: '',
    priority_tier: '' as '' | '1' | '2' | '3',
    deal_type: '' as DealType | '',
    estimated_monthly_value: '',
    lead_source_id: '',
    assigned_sales_owner_id: '',
  });

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [ownersData, leadSourcesData] = await Promise.all([
          projectAPI.getTeamMembers().then(members => members.filter(m => m.is_partner ?? m.is_collective)),
          projectAPI.getLeadSources(),
        ]);
        setOwners(ownersData);
        setLeadSources(leadSourcesData);
      } catch (error) {
        console.error('Failed to load reference data:', error);
      }
    };
    if (open) {
      loadReferenceData();
    }
  }, [open]);

  const resetForm = () => {
    setFormData({
      company_name: '',
      primary_contact_name: '',
      primary_contact_email: '',
      primary_contact_phone: '',
      website: '',
      priority_tier: '',
      deal_type: '',
      estimated_monthly_value: '',
      lead_source_id: '',
      assigned_sales_owner_id: '',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.company_name || !formData.primary_contact_name) return;

    setLoading(true);
    try {
      await projectAPI.createClient({
        company_name: formData.company_name,
        primary_contact_name: formData.primary_contact_name,
        primary_contact_email: formData.primary_contact_email || undefined,
        primary_contact_phone: formData.primary_contact_phone || undefined,
        website: formData.website || undefined,
        priority_tier: formData.priority_tier ? Number(formData.priority_tier) : undefined,
        deal_type: formData.deal_type || undefined,
        estimated_monthly_value: formData.estimated_monthly_value
          ? Number(formData.estimated_monthly_value)
          : undefined,
        lead_source_id: formData.lead_source_id || undefined,
        assigned_sales_owner_id: formData.assigned_sales_owner_id || undefined,
      });
      resetForm();
      onSuccess();
    } catch (error) {
      console.error('Failed to create client:', error);
    } finally {
      setLoading(false);
    }
  };

  const formFields = (
    <div className="grid grid-cols-2 gap-4">
      <div className="col-span-2">
        <Label htmlFor="company_name">Company Name *</Label>
        <Input
          id="company_name"
          placeholder="Acme Inc."
          className={inputSize}
          value={formData.company_name}
          onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
          required
        />
      </div>

      <div className="col-span-2">
        <Label htmlFor="primary_contact_name">Primary Contact *</Label>
        <Input
          id="primary_contact_name"
          placeholder="John Smith"
          className={inputSize}
          value={formData.primary_contact_name}
          onChange={(e) => setFormData({ ...formData, primary_contact_name: e.target.value })}
          required
        />
      </div>

      <div>
        <Label htmlFor="primary_contact_email">Email</Label>
        <Input
          id="primary_contact_email"
          type="email"
          placeholder="john@acme.com"
          className={inputSize}
          value={formData.primary_contact_email}
          onChange={(e) => setFormData({ ...formData, primary_contact_email: e.target.value })}
        />
      </div>

      <div>
        <Label htmlFor="primary_contact_phone">Phone</Label>
        <Input
          id="primary_contact_phone"
          placeholder="(555) 123-4567"
          className={inputSize}
          value={formData.primary_contact_phone}
          onChange={(e) => setFormData({ ...formData, primary_contact_phone: e.target.value })}
        />
      </div>

      <div className="col-span-2">
        <Label htmlFor="website">Website</Label>
        <div className="relative">
          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="website"
            type="url"
            className={`pl-9 ${inputSize}`}
            placeholder="https://example.com"
            value={formData.website}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="priority_tier">Priority Tier</Label>
        <Select
          value={formData.priority_tier}
          onValueChange={(v) => setFormData({ ...formData, priority_tier: v as '1' | '2' | '3' })}
        >
          <SelectTrigger className={selectSize}>
            <SelectValue placeholder="Select tier" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(PRIORITY_TIERS).map(([key, { label, description }]) => (
              <SelectItem key={key} value={key}>
                <div>
                  <div>{label}</div>
                  <div className="text-xs text-muted-foreground">{description}</div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="deal_type">Deal Type</Label>
        <Select
          value={formData.deal_type}
          onValueChange={(v) => setFormData({ ...formData, deal_type: v as DealType })}
        >
          <SelectTrigger className={selectSize}>
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(DEAL_TYPES).map(([key, { label, description }]) => (
              <SelectItem key={key} value={key}>
                <div>
                  <div>{label}</div>
                  <div className="text-xs text-muted-foreground">{description}</div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="estimated_monthly_value">Est. Monthly Value</Label>
        <div className="relative">
          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="estimated_monthly_value"
            type="number"
            className={`pl-8 ${inputSize}`}
            placeholder="5000"
            value={formData.estimated_monthly_value}
            onChange={(e) => setFormData({ ...formData, estimated_monthly_value: e.target.value })}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="lead_source_id">Lead Source</Label>
        <Select
          value={formData.lead_source_id}
          onValueChange={(v) => setFormData({ ...formData, lead_source_id: v })}
        >
          <SelectTrigger className={selectSize}>
            <SelectValue placeholder="Select source" />
          </SelectTrigger>
          <SelectContent>
            {leadSources.map((source) => (
              <SelectItem key={source.id} value={source.id}>
                {source.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="assigned_sales_owner_id">Sales Owner</Label>
        <Select
          value={formData.assigned_sales_owner_id}
          onValueChange={(v) => setFormData({ ...formData, assigned_sales_owner_id: v })}
        >
          <SelectTrigger className={selectSize}>
            <SelectValue placeholder="Assign owner" />
          </SelectTrigger>
          <SelectContent>
            {owners.map((owner) => (
              <SelectItem key={owner.id} value={owner.id}>
                {owner.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const submitButtons = (
    <>
      <Button
        type="button"
        variant="outline"
        className="h-11 sm:h-9"
        onClick={() => onOpenChange(false)}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        className="h-11 sm:h-9"
        disabled={loading || !formData.company_name || !formData.primary_contact_name}
      >
        {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
        Create Client
      </Button>
    </>
  );

  // Phones: bottom drawer with its own scroll area, so the iOS keyboard never
  // buries the lower fields or the submit button.
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} direction="bottom">
        <DrawerContent>
          <DrawerHeader className="px-4 pt-4 pb-2">
            <DrawerTitle className="font-serif">Add New Client</DrawerTitle>
            <DrawerDescription>
              Create a new client in the pipeline. They will start in the Lead stage.
            </DrawerDescription>
          </DrawerHeader>
          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              {formFields}
            </div>
            <DrawerFooter className="flex-row border-t px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] [&>*]:flex-1">
              {submitButtons}
            </DrawerFooter>
          </form>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif">Add New Client</DialogTitle>
          <DialogDescription>
            Create a new client in the pipeline. They will start in the Lead stage.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {formFields}
          <DialogFooter>
            {submitButtons}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
